import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, tap } from 'rxjs';
import { ProductTypeENUM } from 'src/app/models/enums/ProductEnums';
import { ChildAddButtonData } from 'src/app/models/generic-models/views/childAddButtonData';
import { MeasurementLinesAndMeasuredProducts } from 'src/app/models/interfaces/GenericInterfaces';
import { Product } from 'src/app/models/product-models/views/Product';
import { Series } from 'src/app/models/product-models/views/Series';
import { Wall } from 'src/app/models/product-models/views/Wall';
import { environment } from 'src/environments/environment';
import { Camera, Scene, Texture, Vector3, WebGLRenderer } from 'three';
import { ProductAddedEvent } from '../../interfaces/ConfiguratorEvents';
import { MeasurementLine } from '../models/RayMeasurements/MeasurementLine';
import { PreviewData } from './preview-generation-controller.service';
import { ModuleProduct } from 'src/app/models/product-models/views/ModuleProduct';

@Injectable()
export class EventBusService {
  private _sceneChanged: ReplaySubject<Scene> = new ReplaySubject<Scene>(1);
  private _cameraChanged: ReplaySubject<Camera> = new ReplaySubject<Camera>(1);
  private _canvasChanged: ReplaySubject<HTMLCanvasElement> = new ReplaySubject<HTMLCanvasElement>(1);
  private _canvasWithSelectionEvents: ReplaySubject<HTMLCanvasElement> = new ReplaySubject<HTMLCanvasElement>(1);
  private _productRemoved: ReplaySubject<Product> = new ReplaySubject<Product>(1);
  private _productAdded: ReplaySubject<ProductAddedEvent> = new ReplaySubject<ProductAddedEvent>(1);
  private _products: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);
  private _rootProducts: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]); // products that are directly in configuration scene. Basically they
  private _cameraMoved: ReplaySubject<Vector3 | undefined> = new ReplaySubject<Vector3 | undefined>(1);
  private _configuratorWindowSizeChanged: ReplaySubject<HTMLCanvasElement> = new ReplaySubject<HTMLCanvasElement>(1);
  private _seriesChanged: ReplaySubject<Series> = new ReplaySubject<Series>(1);
  private _configurationAdded: ReplaySubject<Product> = new ReplaySubject<Product>(1);
  private _frameRendered: ReplaySubject<void> = new ReplaySubject(1);
  private _childAddButtonsChanged: ReplaySubject<Map<Product, ChildAddButtonData[]>> = new ReplaySubject(1);
  private _controllsLock: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _walls: BehaviorSubject<Wall[]> = new BehaviorSubject<Wall[]>([]);
  private _wallTextureChanged: ReplaySubject<void> = new ReplaySubject(1);
  private _measurementLinesUpdated: ReplaySubject<MeasurementLinesAndMeasuredProducts> =
    new ReplaySubject<MeasurementLinesAndMeasuredProducts>(1);

  private _rendererChanged: ReplaySubject<WebGLRenderer> = new ReplaySubject<WebGLRenderer>(1);
  private _productDefaultSettingsSet: ReplaySubject<Product> = new ReplaySubject<Product>(1);

  private _textureApplied: ReplaySubject<Texture> = new ReplaySubject<Texture>(1);
  private _productHeightChanged: ReplaySubject<Product | undefined> = new ReplaySubject<Product | undefined>(1);

  private _debugViewInputChange = new Subject<void>();

  private _previewsShowRequested = new BehaviorSubject<PreviewData[]>([]);
  private _previewsHideRequested = new BehaviorSubject<PreviewData[]>([]);

  private _placedProductPreviews: BehaviorSubject<Map<PreviewData, Product>> = new BehaviorSubject<
    Map<PreviewData, Product>
  >(new Map<PreviewData, Product>());

  private _explicitRenderRequested: ReplaySubject<boolean> = new ReplaySubject(1);

  private _importingFinished: ReplaySubject<void> = new ReplaySubject(1);

  public sceneChanged$: Observable<Scene>;
  public cameraChanged$: Observable<Camera>;
  public canvasChanged$: Observable<HTMLCanvasElement>;
  public canvasWithSelectionEventsChanged$: Observable<HTMLCanvasElement>; //Separate event for canvas after we add selection events, to ensure it is first event, so we can stop event propagation from it.
  public productAdded$: Observable<ProductAddedEvent>;
  public productRemoved$: Observable<Product>;
  public products$: Observable<Product[]>;
  public rootProducts$: Observable<Product[]>;
  public cameraMoved$: Observable<Vector3 | undefined>;
  public configuratorWindowSizeChanged$: Observable<HTMLCanvasElement>;
  public seriesChanged$: Observable<Series>;
  public configurationAdded$: Observable<Product>;
  public frameRendered$: Observable<void>;
  public childAddButtonsChanged$: Observable<Map<Product, ChildAddButtonData[]>>;
  public controllsLock$: Observable<boolean>;
  public walls$: Observable<Wall[]>;
  public wallTextureChanged$: Observable<void>;
  public measurementLinesUpdated$: Observable<MeasurementLinesAndMeasuredProducts>;
  public rendererChanged$: Observable<WebGLRenderer>;
  public productDefaultSettingsSet$: Observable<Product>;
  public textureApplied$: Observable<Texture>;
  public productHeightChanged$: Observable<Product | undefined>;
  public debugViewInputChange$: Observable<void>;
  public previewsShowRequested$: Observable<PreviewData[]>;
  public previewsHideRequested$: Observable<PreviewData[]>;
  public placedProductPreviews$: Observable<Map<PreviewData, Product>>;
  public explicitRenderRequested$: Observable<boolean>;
  public importingFinished$: Observable<void>;

  constructor() {
    /* eslint-disable no-console*/
    if (environment.debug) {
      this.sceneChanged$ = this._sceneChanged.asObservable().pipe(tap((val) => console.log('scene changed ', val)));

      this.cameraChanged$ = this._cameraChanged.asObservable().pipe(tap((val) => console.log('camera changed ', val)));

      this.configuratorWindowSizeChanged$ = this._configuratorWindowSizeChanged
        .asObservable()
        .pipe(tap((val) => console.log('window size changed ', val)));

      this.canvasChanged$ = this._canvasChanged.asObservable();

      this.canvasWithSelectionEventsChanged$ = this._canvasWithSelectionEvents.asObservable().pipe(
        tap((canvas) => {
          //When a new canvas is used, the size could be different
          console.log('canvas changed ', canvas);
          this.publishConfiguratorWindowSizeChange(canvas);
        }),
      );

      /* const logCallback = (
        event: {
          attribute: MaterialChange;
          attributeValue: MaterialValue;
        } & Event<ProductEventsENUM, Product>,
      ) => {
        console.log(event.target, ' had its material ', event.attribute, ' changed to ', event.attributeValue);
      }; */

      this.productAdded$ = this._productAdded.asObservable(); /* .pipe(
        tap((val) => {
          console.log('product Added ', val);
          val.addEventListener(ProductEventsENUM.MaterialChangeEvent, logCallback);
        }),
      ); */

      this.productRemoved$ = this._productRemoved.asObservable(); /* .pipe(
        tap((val) => {
          console.log('product removed ', val);
          val.removeEventListener(ProductEventsENUM.MaterialChangeEvent, logCallback);
        }),
      ); */

      this.products$ = this._products.asObservable().pipe(tap((val) => console.log('Current products are: ', val)));
      this.rootProducts$ = this._rootProducts
        .asObservable()
        .pipe(tap((val) => console.log('Current products are: ', val)));

      this.cameraMoved$ =
        this._cameraMoved.asObservable() /* .pipe(tap((val) => console.log('camera moved to ', val))) */;

      this.seriesChanged$ = this._seriesChanged.asObservable();
      this.configurationAdded$ = this._configurationAdded.asObservable();
      this.frameRendered$ = this._frameRendered.asObservable();
      this.childAddButtonsChanged$ = this._childAddButtonsChanged.asObservable();
      this.controllsLock$ = this._controllsLock.asObservable();
      this.walls$ = this._walls.asObservable();
      this.wallTextureChanged$ = this._wallTextureChanged.asObservable();
      this.measurementLinesUpdated$ = this._measurementLinesUpdated.asObservable();
      this.rendererChanged$ = this._rendererChanged.asObservable();
      this.productDefaultSettingsSet$ = this._productDefaultSettingsSet.asObservable();
      this.textureApplied$ = this._textureApplied.asObservable();
      this.productHeightChanged$ = this._productHeightChanged.asObservable();
      this.debugViewInputChange$ = this._debugViewInputChange.asObservable();
      this.previewsShowRequested$ = this._previewsShowRequested.asObservable();
      this.previewsHideRequested$ = this._previewsHideRequested.asObservable();
      this.placedProductPreviews$ = this._placedProductPreviews.asObservable();
      this.explicitRenderRequested$ = this._explicitRenderRequested.asObservable();
      this.importingFinished$ = this._importingFinished.asObservable();
    } /* eslint-enable no-console*/ else {
      this.sceneChanged$ = this._sceneChanged.asObservable();
      this.cameraChanged$ = this._cameraChanged.asObservable();
      this.configuratorWindowSizeChanged$ = this._configuratorWindowSizeChanged.asObservable();
      this.canvasChanged$ = this._canvasChanged.asObservable();

      this.canvasWithSelectionEventsChanged$ = this._canvasWithSelectionEvents.asObservable().pipe(
        tap((canvas) => {
          //When a new canvas is used, the size could be different
          this.publishConfiguratorWindowSizeChange(canvas);
        }),
      );

      this.productAdded$ = this._productAdded.asObservable();
      this.productRemoved$ = this._productRemoved.asObservable();
      this.products$ = this._products.asObservable();
      this.rootProducts$ = this._rootProducts.asObservable();
      this.cameraMoved$ = this._cameraMoved.asObservable();
      this.seriesChanged$ = this._seriesChanged.asObservable();
      this.configurationAdded$ = this._configurationAdded.asObservable();
      this.frameRendered$ = this._frameRendered.asObservable();
      this.childAddButtonsChanged$ = this._childAddButtonsChanged.asObservable();
      this.controllsLock$ = this._controllsLock.asObservable();
      this.walls$ = this._walls.asObservable();
      this.wallTextureChanged$ = this._wallTextureChanged.asObservable();
      this.measurementLinesUpdated$ = this._measurementLinesUpdated.asObservable();
      this.rendererChanged$ = this._rendererChanged.asObservable();
      this.productDefaultSettingsSet$ = this._productDefaultSettingsSet.asObservable();
      this.textureApplied$ = this._textureApplied.asObservable();
      this.productHeightChanged$ = this._productHeightChanged.asObservable();
      this.debugViewInputChange$ = this._debugViewInputChange.asObservable();
      this.previewsShowRequested$ = this._previewsShowRequested.asObservable();
      this.previewsHideRequested$ = this._previewsHideRequested.asObservable();
      this.placedProductPreviews$ = this._placedProductPreviews.asObservable();
      this.explicitRenderRequested$ = this._explicitRenderRequested.asObservable();
      this.importingFinished$ = this._importingFinished.asObservable();
    }
  }

  public publishConfiguratorWindowSizeChange(canvas: HTMLCanvasElement) {
    this._configuratorWindowSizeChanged.next(canvas);
  }

  public publicWallTextureChange() {
    this._wallTextureChanged.next();
  }

  public publishScene(scene: Scene) {
    this._sceneChanged.next(scene);
  }

  public publishCamera(camera: Camera) {
    this._cameraChanged.next(camera);
  }

  public publishCanvas(canvas: HTMLCanvasElement) {
    this._canvasChanged.next(canvas);
  }

  public publishCanvasWithSelectionEvents(canvas: HTMLCanvasElement) {
    this._canvasWithSelectionEvents.next(canvas);
  }

  public publishSeries(series: Series) {
    this._seriesChanged.next(series);
  }

  public publishProductHeightChange(product: Product | undefined) {
    this._productHeightChanged.next(product);
  }

  public addProduct(product: Product, addedByUser = false) {
    this._productAdded.next({ product: product, addedByUser: addedByUser });
    const currentProducts = this._products.getValue();
    this._products.next([...currentProducts, product]);

    if (product.productType === ProductTypeENUM.Module) {
      const currentRootProducts = this._rootProducts.getValue();
      this._rootProducts.next([...currentRootProducts, product]);
    }
  }

  public productTypeChanged(product: Product) {
    const currentRootProducts = this._rootProducts.getValue();

    if (product.productType === ProductTypeENUM.Module) {
      this._rootProducts.next([...currentRootProducts, product]);
    } else {
      const updatedRootProducts = currentRootProducts.filter(
        (product) => product.productType === ProductTypeENUM.Module,
      );
      this._rootProducts.next(updatedRootProducts);
    }
  }

  public removeProduct(product: Product) {
    this._productRemoved.next(product);
    const currentProducts = this._products.getValue();
    this._products.next(currentProducts.filter((p) => p.id !== product.id));

    const currentRootProducts = this._rootProducts.getValue();
    this._rootProducts.next(currentRootProducts.filter((p) => p.id !== product.id));
  }

  public publishCameraMove(position?: Vector3) {
    this._cameraMoved.next(position);
  }

  public publishConfigurationAdded(product: Product) {
    this._configurationAdded.next(product);
  }

  public publishFrameRendered() {
    this._frameRendered.next();
  }

  public publishRenderer(renderer: WebGLRenderer) {
    this._rendererChanged.next(renderer);
  }

  public publishChildAddButtonData(data: Map<Product, ChildAddButtonData[]>) {
    this._childAddButtonsChanged.next(data);
  }

  public publishLockToggle(value?: boolean) {
    this._controllsLock.next(value !== undefined ? value : !this._controllsLock.getValue());
  }

  public publishWallsChange(walls: Wall[]) {
    this._walls.next(walls);
  }

  public publishMeasurementLinesUpdated(measurementLines: MeasurementLine[], productsMeasuredFrom: Product[]) {
    this._measurementLinesUpdated.next({ measurementLines: measurementLines, products: productsMeasuredFrom });
  }

  public publishProductDefaultSettingsSet(product: Product) {
    this._productDefaultSettingsSet.next(product);
  }

  public publishTextureApplied(texture: Texture) {
    this._textureApplied.next(texture);
  }

  public publishDebugViewInputChange() {
    this._debugViewInputChange.next();
  }

  private _isRootElement(product: Product) {
    return (
      product.productType === ProductTypeENUM.Module ||
      (product.productType === ProductTypeENUM.Preview &&
        product.productBlueprint.jsonData?.customPreviewType !== undefined)
    );
  }

  public requestPreviewsShow(previewDatas: PreviewData[]) {
    this._previewsShowRequested.next(previewDatas);
  }

  public requestPreviewsHide(previewDatas: PreviewData[]) {
    this._previewsHideRequested.next(previewDatas);
  }

  //Trenutno se observabli ne zavedajo da se je map spremenil, ker ni potrebe. če se pojavi kliči te funkcije namesto direkt v mapu, katerega imajo komponente
  public setPlacedProductPreview(key: PreviewData, value: ModuleProduct) {
    const currentPlacedPreviewProducts = this._placedProductPreviews.getValue();

    const newPlacedPreviewProducts = new Map<PreviewData, Product>(currentPlacedPreviewProducts);
    newPlacedPreviewProducts.set(key, value);
    this._placedProductPreviews.next(newPlacedPreviewProducts);
  }

  public deletePlacedProductPreview(key: PreviewData) {
    const currentPlacedPreviewProducts = this._placedProductPreviews.getValue();

    const newPlacedPreviewProducts = new Map<PreviewData, Product>(currentPlacedPreviewProducts);
    newPlacedPreviewProducts.delete(key);
    this._placedProductPreviews.next(newPlacedPreviewProducts);
  }

  public requestExplicitRender(renderShadows = false) {
    this._explicitRenderRequested.next(renderShadows);
  }

  public publishImportingFinished() {
    this._importingFinished.next();
  }
}
